import { SchemaObject, IOperation, ItemPath, ISchemaObject, IAnnotation, ItemWithPath, Item, Annotation, DeleteOperation, ISignal, SignalOperation } from '@augloop/types-core';
import { IClientMetadata } from '@augloop/schemas/dist/src/client/schemas';
import { IWorkflowSite, IWorkflowContext, IWorkflowActions, IDisposable, SetAnnotationsOptions, WorkflowKind, IWorkflow, AuthTokenType } from '@augloop/interfaces';
import { SingleItemWorkflowLambda } from './single-item-workflow';
import { Log, LogCategory, Operation as LogOperation } from '@augloop/logging/dist/src/client/logging';
import { AnnotationResultsMessage } from '@augloop/session-protocol';
import { ISessionInternal } from './session';

export interface IModelDownloader {
    /**
     * Retrieves the files of the given modelas ArrayBuffers.
     *
     * @param modelId Name of model to be retrieved
     * @param version Version of model to be retrieved
     *
     * @returns Files as map with key as file name and value as an ArrayBuffer of the file
     */
    getResourceAsArrayBuffer(modelId: string, version: string): Promise<{ [key: string]: ArrayBuffer }>;

    /**
     * Retrieves the files of the given model as ObjectURL.
     *
     * @param modelId Name of model to be retrieved
     * @param version Version of model to be retrieved
     *
     * @returns Files as map with key as file name and value as ObjectURL to file
     */
    getResourceAsURL(modelId: string, version: string): Promise<{ [key: string]: string }>;
}

type WorkflowImplementation = {
    workflow: IWorkflow;
    workflowLambda: IDisposable;
    initPromise: Promise<void>;
}

export class LocalWorkflowExecutionManager {
    private localWorkflows: WorkflowImplementation[] = [];
    private session: ISessionInternal;
    private nextAnnotationId: number;
    private nextSignalId: number;
    private modelDownloader: IModelDownloader;

    constructor(session: ISessionInternal, modelDownloader: IModelDownloader) {
        this.session = session;
        this.nextAnnotationId = 1;
        this.nextSignalId = 1;
        this.modelDownloader = modelDownloader;
    }

    public getNextClientAnnotationId(): string {
        return '#AC' + this.nextAnnotationId++;
    }

    public getNextClientSignalId(): string {
        return '#SC' + this.nextSignalId++;
    }

    public registerLocalWorkflow(workflow: IWorkflow): void {
        const op = new LogOperation({
            operationName: 'WorkflowRegistration',
            resourceId: workflow.id
        }).start();
        if (workflow.inputTypes.length === 0) {
            throw new Error(`Invalid workflow params`);
        } else {
            const workflowLambda = workflow.factory();
            const wfImplementation: WorkflowImplementation = {
                workflow: workflow,
                workflowLambda: workflowLambda,
                initPromise: this.initWorkflow(workflow.kind, workflowLambda)
            };
            this.localWorkflows.push(wfImplementation);
            op.success = true;
            Log.info(0x2224d0de /* tag_8jnd4 */, LogCategory.CoreDefault, op.stop());
        }
    }

    public runLocalWorkflows(ops: IOperation[]): void {
        for (const op of ops) {
            const opType = SchemaObject.getTypeNameFor(op);
            // Check that the item(s) in question is not being deleted
            // Currently, AL cache is not stored/available locally
            if (opType === DeleteOperation.getTypeName()) {
                continue;
            }
            for (const opItem of op.items) {
                if (!opItem.body) {
                    continue;
                }
                for (const workflowImplementation of this.localWorkflows) {
                    if (SchemaObject.matchesTypesFor(opItem.body, workflowImplementation.workflow.inputTypes)) {
                        const item: ItemWithPath = {
                            parentPath: op.parentPath,
                            id: opItem.id,
                            body: opItem.body,
                            revId: opItem.revId,
                            source: opItem.source
                        };
                        this.executeLocalWorkflow(workflowImplementation, [item], this.session.getClientMetadata()).catch((error) => {
                            Log.error(0x2224d0df /* tag_8jnd5 */, LogCategory.CoreDefault, error);
                        });
                    }
                }
            }
        }
    }

    private getResourceAsArrayBuffer(modelId: string, version: string): Promise<{ [key: string]: ArrayBuffer }> {
        if (this.modelDownloader) {
            return this.modelDownloader.getResourceAsArrayBuffer(modelId, version);
        } else {
            return Promise.reject(new Error('Resource Downloader never created'));
        }
    }
    private getResourceAsURL(modelId: string, version: string): Promise<{ [key: string]: string }> {
        if (this.modelDownloader) {
            return this.modelDownloader.getResourceAsURL(modelId, version);
        } else {
            return Promise.reject(new Error('Resource Downloader never created'));
        }
    }

    private getToken(_tokenType: AuthTokenType, _cb: (error: Error, token: string, returnedTokenType?: AuthTokenType) => void) {
        throw new Error('Not implemented');
    }

    private initWorkflow(workflowKind: WorkflowKind, workflowLambda: IDisposable): Promise<void> {
        if (workflowKind == WorkflowKind.SingleItem) {
            const site: IWorkflowSite = {
                getResourceAsArrayBuffer: this.getResourceAsArrayBuffer.bind(this),
                getResourceAsURL: this.getResourceAsURL.bind(this),
            };
            return (workflowLambda as SingleItemWorkflowLambda<ISchemaObject>).init(site);
        } else {
            return Promise.resolve();
        }
    }

    private executeLocalWorkflow(
        workflowInfo: WorkflowImplementation,
        inputItems: ItemWithPath[],
        clientMetadata: IClientMetadata): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const workflow = workflowInfo.workflow;
            const executeOp = new LogOperation({
                operationName: 'ExecuteWorkflow',
                resourceId: workflow.id
            });
            executeOp.start();

            const endOp = (error?: string) => {
                if (error) {
                    executeOp.resultDescription = error;
                    executeOp.resultSignature = 'Exception';
                    Log.error(0x2224d0e0 /* tag_8jnd6 */, LogCategory.CoreDefault, executeOp.stop());
                } else {
                    executeOp.success = true;
                    Log.info(0x2224d0e1 /* tag_8jnd7 */, LogCategory.CoreDefault, executeOp.stop());
                }
                return error;
            };

            const setAnnotations = (obj: ISchemaObject, annotationType: string, annotations: IAnnotation[], options?: SetAnnotationsOptions) => {
                // Has to be true since only single-item workflows are currently supported
                if (inputItems[0].body !== obj) {
                    reject(endOp(`Expected obj to be ${inputItems[0].body} but instead it was ${obj}`));
                    return;
                }
                const item: ItemWithPath = inputItems[0];

                let error: string;
                // Check that annotations match expectations - otherwise workflow author must fix
                if (!Array.isArray(annotations)) {
                    error = `Workflow produced an invalid annotation array`;
                } else if (!workflow.outputTypes || !workflow.outputTypes.includes(annotationType)) {
                    error = `Workflow said it would output one of [${workflow.outputTypes}] but instead output ${annotationType}`;
                } else if (!item) {
                    error = `No item provided`;
                } else {
                    for (const annotation of annotations) {
                        if (!SchemaObject.matchesTypesFor(annotation, [Annotation.getTypeName()])) {
                            error = `Workflow produced an output that is not an annotation ${SchemaObject.getTypeNameFor(annotation)}`;
                        } else if (SchemaObject.getTypeNameFor(annotation) !== annotationType) {
                            error = `Workflow produced inconsistent annotation types in setAnnotations call (${SchemaObject.getTypeNameFor(annotation)} did not match expected ${annotationType})`;
                        }
                    }
                }
                if (error) {
                    reject(endOp(error));
                    return;
                }

                // Path to where the annotation will be added
                let path: ItemPath;
                if (options && options.isSessionAnnotation) {
                    path = ['session'];
                } else if (options && options.ancestorType) {
                    path = item.parentPath;
                } else {
                    path = item.parentPath.concat(item.id);
                }

                // Changing annotations into items
                const annotationItemsArray: Item[] = [];
                for (const annotation of annotations) {
                    annotationItemsArray.push({
                        id: this.getNextClientAnnotationId(),
                        source: workflow.id,
                        body: annotation
                    });
                }

                this.session.onAnnotationResults(new AnnotationResultsMessage({
                    annotationType: annotationType,
                    ops: [{ parentPath: path, items: annotationItemsArray }]
                }),
                    () => { }); // Empty response callback because we don't need to acknowledge annotations are received
            };

            const submitSignals = (signals: ISignal[]) => {
                // Changing signals into items
                const signalItemsArray: Item[] = signals.map(signal => {
                    return {
                        id: this.getNextClientSignalId(),
                        source: workflow.id,
                        body: signal
                    };
                });
                const submitOp: SignalOperation = new SignalOperation({
                    parentPath: ['session'],
                    items: signalItemsArray
                });
                this.session.submitOperations([submitOp]);
            };

            const context: IWorkflowContext = {
                getToken: this.getToken.bind(this),
                clientMetadata: clientMetadata,
                site: {
                    getResourceAsArrayBuffer: this.getResourceAsArrayBuffer.bind(this),
                    getResourceAsURL: this.getResourceAsURL.bind(this),
                },
            };

            const site: IWorkflowSite = {
                getResourceAsArrayBuffer: this.getResourceAsArrayBuffer.bind(this),
                getResourceAsURL: this.getResourceAsURL.bind(this),
            };

            const workflowExecutionDone = (err?: Error) => {
                endOp(err ? err.message : null);
                if (err) {
                    reject(err);
                } else {
                    resolve();
                }
            };

            const actions: IWorkflowActions = {
                setAnnotations: setAnnotations,
                submitSignals: submitSignals,
                done: workflowExecutionDone
            };

            const workflowInstance: IDisposable = workflowInfo.workflowLambda;
            if (workflow.kind === WorkflowKind.SingleItem) {
                if (inputItems.length !== 1) {
                    endOp('Single item workflows expect a single input');
                }
                (context as any).delta = inputItems[0].delta;

                try {
                    const singleItemWorkflow = workflowInstance as SingleItemWorkflowLambda<ISchemaObject>;
                    if (!workflowInfo.initPromise) {
                        workflowInfo.initPromise = singleItemWorkflow.init(site);
                    }
                    workflowInfo.initPromise.then(() => {
                        singleItemWorkflow.execute(inputItems[0].body, context, actions);
                    }).catch((error) => {
                        endOp(error);
                    });
                } catch (error) {
                    endOp(error);
                }
            } else {
                endOp(`Only single item workflows supported currently`);
            }
        });
    }
}
